import React, { useState } from 'react';
import { Box, Heading, Text, Button, VStack, Flex, Icon, SimpleGrid, useColorModeValue } from '@chakra-ui/react';
import { FaHandsHelping, FaBrain, FaChild, FaPaintBrush, FaEdit, FaImage } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import { EditableText, EditableImage } from '../components/EditableContent';

const Home = () => {
  const { user } = useAuth();
  const { content, isOwner, isEditMode, toggleEditMode, uploadImage, updateContent } = useContent();
  const [isHeroImageEditing, setIsHeroImageEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const bgColor = useColorModeValue('white', 'gray.800');
  
  // Hero image upload handler
  const handleHeroImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setIsUploading(true);
      const result = await uploadImage(file, 'hero');
      
      if (result.success) {
        await updateContent('hero', 'image', result.url);
        setIsHeroImageEditing(false);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };
  
  // Get the hero image URL from content state
  const heroImageUrl = content.hero?.image || '/Playground.JPEG';
  
  return (
    <Box>
      {/* Admin controls for owner */}
      {isOwner && (
        <Box 
          position="fixed" 
          top="20px" 
          right="20px" 
          zIndex="1000"
          bg={bgColor}
          p={3}
          borderRadius="md"
          shadow="md"
        >
          <Button
            size="sm"
            leftIcon={<FaEdit />}
            colorScheme={isEditMode ? "red" : "blue"}
            onClick={toggleEditMode}
            mb={isEditMode ? 2 : 0}
          >
            {isEditMode ? "Exit Edit Mode" : "Edit Content"}
          </Button>
          
          {/* Special Hero Image Edit Button - Only visible in edit mode */}
          {isEditMode && (
            <VStack align="stretch" spacing={2} mt={2}>
              <Button
                size="sm"
                leftIcon={<FaImage />}
                colorScheme="teal"
                onClick={() => setIsHeroImageEditing(true)}
              >
                Edit Hero Image
              </Button>
              
              {/* Hero Image Upload Panel */}
              {isHeroImageEditing && (
                <Box
                  position="absolute"
                  top="100%"
                  right="0"
                  mt={2}
                  bg="white"
                  p={4}
                  borderRadius="md"
                  shadow="md"
                  zIndex={1001}
                >
                  <Text mb={2}>Upload new hero image:</Text>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleHeroImageChange}
                    style={{ display: 'none' }}
                    id="hero-image-upload"
                  />
                  <Button
                    as="label"
                    htmlFor="hero-image-upload"
                    colorScheme="blue"
                    isLoading={isUploading}
                    mb={2}
                    cursor="pointer"
                  >
                    Choose Image
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => setIsHeroImageEditing(false)}
                    ml={2}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </VStack>
          )}
        </Box>
      )}
      
      {/* Hero Section */}
      <Box position="relative" id="hero">
        <Box
          bgImage={`url(${heroImageUrl})`}
          bgSize="cover"
          bgPosition="center"
          color="white"
          py={{ base: '80px', md: '120px' }}
          textAlign="center"
          position="relative"
          zIndex={1}
        >
          <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0, 0, 0, 0.5)" zIndex={-1}></Box>
          <EditableText
            as={Heading}
            section="hero"
            contentKey="title"
            fontSize={{ base: '3xl', md: '5xl' }}
            textShadow="2px 2px 4px rgba(0, 0, 0, 0.7)"
          />
          <EditableText
            section="hero"
            contentKey="subtitle"
            fontSize={{ base: 'md', md: 'lg' }}
            mt={4}
            textShadow="1px 1px 2px rgba(0, 0, 0, 0.7)"
          />
          <Link to="/register">
            <Button bgColor="#afda2d" color="white" size="lg" mt={8}>
              Join the Waitlist
            </Button>
          </Link>
        </Box>
      </Box>

      {/* About Section */}
      <Box py={{ base: '40px', md: '60px' }} px={{ base: '20px', md: '40px' }} textAlign="center" id="about-us">
        <Heading fontSize={{ base: '2xl', md: '4xl' }}>About Us</Heading>
        <EditableText
          section="about"
          contentKey="description"
          fontSize={{ base: 'md', md: 'lg' }}
          mt={4}
        />
        <EditableImage
          section="about"
          contentKey="image1"
          alt="Children engaged in a therapy session at Tiny Triumphs Therapy Center"
          mt={8}
          borderRadius="lg"
          shadow="md"
        />
        <EditableImage
          section="about"
          contentKey="image2"
          alt="Children engaged in a therapy session at Tiny Triumphs Therapy Center"
          mt={8}
          borderRadius="lg"
          shadow="md"
        />
      </Box>

      {/* Services Section */}
      <Box bg="gray.100" py={{ base: '40px', md: '60px' }} px={{ base: '20px', md: '40px' }} textAlign="center" id="our-services">
        <EditableText
          as={Heading}
          section="services"
          contentKey="title"
          fontSize={{ base: '2xl', md: '4xl' }}
        />
        <Flex wrap="wrap" justify="center" mt={8} spacing={8}>
          <VStack bg="white" p={5} borderRadius="lg" shadow="md" m={4} maxW="300px">
            <Icon as={FaHandsHelping} w={10} h={10} color="teal.500" />
            <Heading fontSize="xl" mt={4}>Occupational Therapy</Heading>
            <Text mt={2} fontSize="md">
              Helping children develop the skills they need for daily living.
            </Text>
          </VStack>
          <VStack bg="white" p={5} borderRadius="lg" shadow="md" m={4} maxW="300px">
            <Icon as={FaBrain} w={10} h={10} color="teal.500" />
            <Heading fontSize="xl" mt={4}>Speech Therapy</Heading>
            <Text mt={2} fontSize="md">
              Enhancing communication skills and language development.
            </Text>
          </VStack>
          <VStack bg="white" p={5} borderRadius="lg" shadow="md" m={4} maxW="300px">
            <Icon as={FaChild} w={10} h={10} color="teal.500" />
            <Heading fontSize="xl" mt={4}>Physical Therapy</Heading>
            <Text mt={2} fontSize="md">
              Improving physical abilities and promoting mobility.
            </Text>
          </VStack>
          <VStack bg="white" p={5} borderRadius="lg" shadow="md" m={4} maxW="300px">
            <Icon as={FaPaintBrush} w={10} h={10} color="teal.500" />
            <Heading fontSize="xl" mt={4}>Play School</Heading>
            <Text mt={2} fontSize="md">
              Socialization, arts and crafts, little scientists, and more.
            </Text>
          </VStack>
        </Flex>
      </Box>

      {/* Facilities Section */}
      <Box py={{ base: '40px', md: '60px' }} px={{ base: '20px', md: '40px' }} textAlign="center" id="our-facilities">
        <EditableText
          as={Heading}
          section="facilities"
          contentKey="title"
          fontSize={{ base: '2xl', md: '4xl' }}
        />
        <EditableText
          section="facilities"
          contentKey="description"
          fontSize={{ base: 'md', md: 'lg' }}
          mt={4}
        />

        <Box mt={8}>
          <EditableImage
            section="facilities"
            contentKey="image1"
            alt="Modern and well-equipped therapy facilities at Tiny Triumphs"
            borderRadius="lg"
            shadow="md"
            mb={6}
          />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <EditableImage
              section="facilities"
              contentKey="image2"
              alt="Warm and welcoming reception area at Tiny Triumphs"
              borderRadius="lg"
              shadow="md"
            />
            <EditableImage
              section="facilities"
              contentKey="image3"
              alt="Bright and inviting hallway leading to therapy rooms"
              borderRadius="lg"
              shadow="md"
            />
          </SimpleGrid>
        </Box>
      </Box>

      {/* Contact Section */}
      <Box bg="#34b4e2" color="white" py={{ base: '40px', md: '60px' }} px={{ base: '20px', md: '40px' }} textAlign="center" id="contact-us">
        <EditableText
          as={Heading}
          section="contact"
          contentKey="title"
          fontSize={{ base: '2xl', md: '4xl' }}
        />
        <EditableText
          section="contact"
          contentKey="description"
          fontSize={{ base: 'md', md: 'lg' }}
          mt={4}
        />
        <VStack mt={8} spacing={4}>
          <EditableText
            section="contact"
            contentKey="address"
            fontSize={{ base: 'md', md: 'lg' }}
          />
          <EditableText
            section="contact"
            contentKey="email"
            fontSize={{ base: 'md', md: 'lg' }}
          />
          <EditableText
            section="contact"
            contentKey="phone"
            fontSize={{ base: 'md', md: 'lg' }}
          />
        </VStack>
      </Box>
    </Box>
  );
};

export default Home;